import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation, useSearchParams } from "react-router-dom";
import { api, addGuestToken } from "../lib/api";
import { formatEtaWindow } from "../lib/eta";
import { ChevronLeft, Package, Truck, Home, Clock, CheckCircle2, DollarSign, Copy, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const STAGE_ORDER = ["awaiting_payment", "received", "packed", "shipped", "delivered"];
const STAGE_META = {
  awaiting_payment: { label: "Awaiting payment", icon: Clock, color: "text-slate-500", bg: "bg-slate-100" },
  received: { label: "Received by supplier", icon: Package, color: "text-blue-700", bg: "bg-blue-100" },
  packed: { label: "Packed", icon: Package, color: "text-amber-700", bg: "bg-amber-100" },
  shipped: { label: "Shipped", icon: Truck, color: "text-purple-700", bg: "bg-purple-100" },
  delivered: { label: "Delivered", icon: Home, color: "text-emerald-700", bg: "bg-emerald-100" },
};

export const OrderDetail = () => {
  const { id } = useParams();
  const isGuest = useLocation().pathname.startsWith("/track");
  const [params] = useSearchParams();
  const linkToken = params.get("t");
  if (linkToken) {
    addGuestToken(linkToken);
    window.history.replaceState(null, "", window.location.pathname);
  }
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const r = await api.get(`/orders/${id}`); setData(r.data); } catch { toast.error("Could not load order"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [id]);

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-20 text-slate-500">Loading order…</div>;
  if (!data) return <div className="max-w-4xl mx-auto px-6 py-20">Order not found.</div>;

  const { order, events } = data;
  const currentStageIdx = STAGE_ORDER.indexOf(order.fulfillment_status);
  const copyOrderId = () => { navigator.clipboard.writeText(order.order_id); toast.success("Order ID copied"); };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <Link to={isGuest ? "/shop" : "/orders"} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-indigo-600 mb-6" data-testid="order-back-link">
        <ChevronLeft className="w-4 h-4" /> {isGuest ? "Keep shopping" : "All orders"}
      </Link>

      <div className="p-6 rounded-3xl bg-gradient-to-br from-indigo-600 to-purple-700 text-white mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider font-bold opacity-80">Order</div>
            <button onClick={copyOrderId} className="font-mono text-sm inline-flex items-center gap-1 hover:opacity-90" data-testid="order-copy-id"><Copy className="w-3 h-3" />{order.order_id}</button>
            <div className="font-display text-2xl sm:text-3xl font-extrabold mt-1">{STAGE_META[order.fulfillment_status]?.label}</div>
            <div className="text-sm opacity-90 mt-1">{new Date(order.created_at).toLocaleString()}</div>
            {order.fulfillment_status !== "delivered" && formatEtaWindow(order) && (
              <div className="inline-flex items-center gap-1.5 mt-3 bg-white/20 px-3 py-1.5 rounded-full text-xs font-semibold" data-testid="order-detail-eta">
                <CalendarDays className="w-3 h-3" />Estimated delivery {formatEtaWindow(order)}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider font-bold opacity-80">Total</div>
            <div className="font-display text-3xl font-extrabold">${order.total.toFixed(2)}</div>
            <div className="text-sm mt-1">
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${order.payment_status === "paid" ? "bg-white/25" : "bg-amber-200/25"}`}>{order.payment_status}</span>
              {order.is_wholesale && <span className="ml-1 inline-block px-2 py-0.5 rounded-full text-xs bg-white/25 font-semibold">wholesale</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="p-6 bg-white rounded-2xl border border-slate-200 mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">Fulfillment progress</div>
        <div className="flex items-center gap-2" data-testid="order-detail-stepper">
          {STAGE_ORDER.slice(1).map((s, i) => {
            const meta = STAGE_META[s];
            const done = i + 1 <= currentStageIdx;
            const active = i + 1 === currentStageIdx;
            const Icon = meta.icon;
            return (
              <React.Fragment key={s}>
                <div className={`flex flex-col items-center ${done ? "" : "opacity-60"}`}>
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${done ? `${meta.bg} ${meta.color}` : "bg-slate-100 text-slate-400"} ${active ? "ring-4 ring-indigo-200 animate-pulse" : ""}`}>
                    {done && !active ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className="text-[10px] font-semibold mt-1 text-center max-w-[80px]">{meta.label}</div>
                </div>
                {i < STAGE_ORDER.length - 2 && <div className={`flex-1 h-1 rounded ${i + 1 < currentStageIdx ? "bg-indigo-500" : "bg-slate-200"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Items */}
        <div className="lg:col-span-3 p-6 bg-white rounded-2xl border border-slate-200">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Items ({order.items.length})</div>
          <div className="space-y-3">
            {order.items.map((it, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50" data-testid={`order-item-${i}`}>
                <img src={it.image} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1">
                  <div className="font-semibold">{it.name}</div>
                  <div className="text-xs text-slate-500">{it.quantity} × ${it.unit_price.toFixed(2)}{it.bulk && <span className="ml-1 text-indigo-600 font-semibold">· bulk</span>}</div>
                </div>
                <div className="font-display font-bold">${(it.unit_price * it.quantity).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4 pt-4 border-t border-slate-100 text-sm">
            <span className="text-slate-600">Subtotal</span>
            <span className="font-semibold">${order.subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-slate-600">Shipping</span>
            <span className="text-emerald-600 font-semibold">Free · auto-fulfilled</span>
          </div>
          <div className="flex justify-between font-display font-extrabold text-lg mt-3 pt-3 border-t border-slate-200">
            <span>Total</span>
            <span>${order.total.toFixed(2)}</span>
          </div>
        </div>

        {/* Timeline */}
        <div className="lg:col-span-2 p-6 bg-white rounded-2xl border border-slate-200">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Wholesaler timeline</div>
          {events.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No events yet — this order will start updating after payment.</div>
          ) : (
            <ol className="relative border-l-2 border-slate-100 ml-2 space-y-4">
              {events.map((e) => {
                const meta = STAGE_META[e.stage] || STAGE_META.received;
                const Icon = meta.icon;
                return (
                  <li key={e.event_id} className="pl-4 -ml-[9px]" data-testid={`event-${e.event_id}`}>
                    <span className={`absolute -left-[9px] w-4 h-4 rounded-full flex items-center justify-center ${meta.bg}`}>
                      <span className={`w-2 h-2 rounded-full ${meta.color.replace("text-", "bg-")}`} />
                    </span>
                    <div className="text-xs font-mono text-slate-400">{new Date(e.at).toLocaleString()}</div>
                    <div className="font-semibold capitalize text-sm inline-flex items-center gap-1 mt-0.5"><Icon className={`w-3 h-3 ${meta.color}`} />{e.stage.replace("_", " ")}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{e.message}</div>
                  </li>
                );
              })}
            </ol>
          )}
          {order.supplier_order_id && (
            <div className="mt-6 p-3 bg-slate-50 rounded-xl text-xs">
              <div className="text-slate-500 uppercase tracking-wider font-bold">Supplier reference</div>
              <div className="font-mono mt-1 break-all">{order.supplier_order_id}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
